// client/src/index.js
import React from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import App from './App';

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <React.StrictMode>
    <App />
  </React.StrictMode>
);

// Register the service worker (PWA) in production
if ('serviceWorker' in navigator && process.env.NODE_ENV === 'production') {
  let updating = false;
  let updatePending = false;

  const showUpdatingOverlay = () => {
    if (updating) return;
    updating = true;
    const overlay = document.createElement('div');
    overlay.id = 'app-update-overlay';
    overlay.style.cssText = 'position:fixed;top:0;left:0;right:0;bottom:0;background:rgba(0,0,0,0.85);color:#fff;display:flex;flex-direction:column;align-items:center;justify-content:center;z-index:999999;font-family:sans-serif;';
    overlay.innerHTML = '<div style="font-size:22px;font-weight:600">🔄 Updating to the latest version…</div><div style="font-size:14px;margin-top:8px;opacity:0.7">Please wait a moment</div>';
    document.body.appendChild(overlay);
  };

  // A small, non-intrusive banner shown while the user is mid-game. We defer the
  // reload until they finish so we never interrupt a live game.
  const showUpdateAvailableToast = () => {
    if (document.getElementById('update-available-toast')) return;
    const toast = document.createElement('div');
    toast.id = 'update-available-toast';
    toast.style.cssText = 'position:fixed;top:0;left:0;right:0;z-index:999998;background:#ffb300;color:#1a1a1a;padding:8px 12px;text-align:center;font-family:sans-serif;font-size:13px;font-weight:600;';
    toast.textContent = '🔄 Update available — it will apply automatically after your game';
    document.body.appendChild(toast);
  };

  // Is the user currently on a game screen (playing or in a waiting room)?
  const isInGame = () => {
    const p = window.location.pathname;
    return p.startsWith('/game/') || p.includes('/waiting/') || p.startsWith('/custom-room/') || p.startsWith('/multiplayer/game/');
  };

  // Wipe every cached file (old builds' JS/CSS/images) BEFORE reloading, so each update replaces the
  // previous version instead of piling up on the phone. Same effect as "uninstall + reinstall" for storage.
  const clearOldCaches = async () => {
    try {
      if (window.caches && caches.keys) {
        const keys = await caches.keys();
        await Promise.all(keys.map((k) => caches.delete(k)));
      }
    } catch (e) { /* ignore */ }
  };

  const applyUpdate = () => {
    showUpdatingOverlay();
    clearOldCaches().finally(() => setTimeout(() => window.location.reload(), 800));
  };

  // Called whenever a new build is detected. If the user is mid-game, defer the
  // reload (and show a small banner); otherwise apply it immediately.
  const onNewVersionDetected = () => {
    if (isInGame()) {
      updatePending = true;
      showUpdateAvailableToast();
      console.log('🔔 Update available — deferred until the game ends');
    } else {
      applyUpdate();
    }
  };

  // Check if the deployed build has a newer JS bundle than the one currently
  // loaded. This is a direct, reliable version check (works even where the
  // service-worker controllerchange event is not fired).
  const checkForNewVersion = async () => {
    try {
      const currentScript = document.querySelector('script[src*="/static/js/main."]');
      const currentHash = currentScript?.getAttribute('src')?.match(/main\.([a-f0-9]+)\.js/)?.[1];
      if (!currentHash) return;
      const res = await fetch('/asset-manifest.json', { cache: 'no-store' });
      if (!res.ok) return;
      const manifest = await res.json();
      const mainEntry = manifest?.files?.['main.js'] || manifest?.entrypoints?.find((e) => e.includes('main.'));
      const latestHash = (mainEntry || '').match(/main\.([a-f0-9]+)\.js/)?.[1];
      if (latestHash && latestHash !== currentHash) {
        console.log(`🔄 New build detected (${currentHash} → ${latestHash})`);
        onNewVersionDetected();
      }
    } catch (e) { /* ignore network errors */ }
  };

  // Poll: apply a deferred update as soon as the user leaves the game screen.
  setInterval(() => {
    if (updatePending && !isInGame()) {
      updatePending = false;
      applyUpdate();
    }
  }, 3000);

  window.addEventListener('load', () => {
    // Track whether the page was already controlled by a service worker, so we
    // only auto-reload on a REAL update (not on the very first install).
    let hadController = !!navigator.serviceWorker.controller;

    navigator.serviceWorker
      .register('/sw.js')
      .then((registration) => {
        console.log('✅ Service worker registered');

        // When a NEW service worker takes control (a new build was deployed),
        // trigger the same deferred update flow. sw.js calls skipWaiting() +
        // clients.claim(), which triggers this controllerchange.
        navigator.serviceWorker.addEventListener('controllerchange', () => {
          const isUpdate = hadController;
          hadController = !!navigator.serviceWorker.controller;
          if (isUpdate) {
            console.log('🔄 New app version detected');
            onNewVersionDetected();
          }
        });

        // Check for updates on load, when the page becomes visible again, and
        // periodically — so returning users always get the latest build.
        const checkUpdate = () => {
          try { registration.update(); } catch (e) { /* ignore */ }
        };
        checkUpdate();
        checkForNewVersion();
        document.addEventListener('visibilitychange', () => {
          if (document.visibilityState === 'visible') {
            checkUpdate();
            checkForNewVersion();
          }
        });
        setInterval(checkForNewVersion, 5 * 60 * 1000); // every 5 minutes
      })
      .catch((err) => console.log('⚠️ Service worker registration failed:', err));
  });
}